import React, { useMemo } from "react";
import { Box, Divider, Paper, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DEFAULT_QUOTATION_INTRO,
  DEFAULT_PAYMENT_TERMS,
  DEFAULT_TERMS_AND_CONDITIONS,
} from "../../constants/quotationPdfDefaults";
import { computeAceQuotationTotals } from "../../utils/aceQuotationTotals";
import { formatInr } from "../../utils/quotationPdfTotals";
import { currencySymbol } from "../../utils/currency";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2.5, 3),
    marginTop: theme.spacing(0),
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    /** Do not clip: overflow:hidden was clipping outlines / focus rings and worsening overlap illusions */
    overflow: "visible",
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionTitleFirst: {
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  subSectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  /** Equal columns; minmax(0,1fr) prevents date/number inputs from forcing uneven widths */
  rowEq3: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    width: "100%",
    gap: theme.spacing(2.5),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  rowEq4: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    width: "100%",
    gap: theme.spacing(2.5),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },
  /** From / To: full width stacked on small screens; two equal columns from md up */
  fromToRow: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    width: "100%",
    gap: theme.spacing(2.5),
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  /** Seven charge fields: row of 4, then row of 3 equal columns (full width, no stray cells) */
  chargeRow4: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    width: "100%",
    gap: theme.spacing(2.5),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.up("lg")]: {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },
  chargeRow3: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    width: "100%",
    marginTop: theme.spacing(2.5),
    gap: theme.spacing(2.5),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.up("lg")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  /**
   * minWidth:0 is required for grid/flex children so inputs don’t overflow the column.
   * Strip default FormControl vertical margins so parent gap is the only vertical rhythm (avoids stacked overlap).
   */
  fieldCell: {
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
    position: "relative",
    "& .MuiTextField-root": {
      width: "100%",
      maxWidth: "100%",
    },
    "& .MuiFormControl-root": {
      width: "100%",
      maxWidth: "100%",
    },
    "& .MuiFormControl-marginNormal": {
      marginTop: 0,
      marginBottom: 0,
    },
    "& .MuiFormControl-marginDense": {
      marginTop: 0,
      marginBottom: 0,
    },
    "& .MuiOutlinedInput-root": {
      maxWidth: "100%",
    },
    "& .MuiInputLabel-outlined": {
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
  },
  previewBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    borderRadius: theme.shape.borderRadius,
    width: "100%",
    boxSizing: "border-box",
  },
  notesTwoCol: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2.5),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  validityRow: {
    display: "grid",
    alignItems: "start",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2.5),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  /** Vertical stack — avoids MUI Grid spacing negative margins bleeding outside the card */
  notesStack: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2.5),
  },
}));

export function getDefaultQuotationDocState() {
  return {
    customerName: "",
    quotationRefNo: "",
    quotationDate: new Date().toISOString().slice(0, 10),
    fromLocation: "",
    toLocation: "",
    distanceKm: "",
    suggestedTruckSize: "",
    loadDescription: "",
    totalCft: "",
    declaredLuggageValue: "",
    carrierRiskPercent: "2",
    gstPercent: "18",
    gstAmount: "",
    transportationChargesAmount: "",
    carTransportationChargesAmount: "",
    loadingChargesAmount: "",
    unloadingChargesAmount: "",
    unpackingRearrangingChargesAmount: "",
    shuttleLongCarryEscortChargesAmount: "",
    carrierRiskChargesAmount: "",
    paymentTerms: DEFAULT_PAYMENT_TERMS,
    quotationIntro: DEFAULT_QUOTATION_INTRO,
    quotationPdfNote: "",
    handymanServicesNote: "",
    extraHandlingAndStorageNote: "",
    localUnionOctroiNote: "",
    validityDays: "",
    moveWindowDays: "",
    transitTimeNote: "",
    termsAndConditionsText: DEFAULT_TERMS_AND_CONDITIONS.join("\n"),
    quotationExtraNote1: "",
    quotationExtraNote2: "",
  };
}

const QuotationPricingSection = ({ doc, setDoc }) => {
  const classes = useStyles();
  const update = (key, value) => setDoc((prev) => ({ ...prev, [key]: value }));

  const preview = useMemo(() => computeAceQuotationTotals(doc), [doc]);

  return (
    <Paper className={classes.paper} elevation={1}>
      <Typography variant="h6">ACE quotation PDF</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Ref no., customer name, and From/To match the first part of the PDF. From/To can be prefilled from origin and
        destination addresses.
      </Typography>

      <Typography className={classes.sectionTitleFirst} variant="subtitle1">
        1. Ref no., customer name &amp; date
      </Typography>
      <Box className={classes.rowEq3}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Quotation ref no."
            variant="outlined"
            size="small"
            value={doc.quotationRefNo}
            onChange={(e) => update("quotationRefNo", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Customer name"
            variant="outlined"
            size="small"
            value={doc.customerName}
            onChange={(e) => update("customerName", e.target.value)}
            required
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Quotation date"
            variant="outlined"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={doc.quotationDate}
            onChange={(e) => update("quotationDate", e.target.value)}
          />
        </Box>
      </Box>

      <Typography className={classes.sectionTitle} variant="subtitle1">
        2. Route &amp; move info
      </Typography>
      <Typography className={classes.subSectionTitle}>From &amp; To (PDF)</Typography>
      <Box className={classes.fromToRow}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="From"
            variant="outlined"
            size="small"
            multiline
            minRows={5}
            value={doc.fromLocation}
            onChange={(e) => update("fromLocation", e.target.value)}
            placeholder="Address line 1, line 2, city, state, pincode"
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="To"
            variant="outlined"
            size="small"
            multiline
            minRows={5}
            value={doc.toLocation}
            onChange={(e) => update("toLocation", e.target.value)}
            placeholder="Address line 1, line 2, city, state, pincode"
          />
        </Box>
      </Box>

      <Typography className={classes.subSectionTitle}>Distance, load &amp; CFT</Typography>
      <Box className={classes.rowEq3}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Distance (Kms)"
            variant="outlined"
            size="small"
            value={doc.distanceKm}
            onChange={(e) => update("distanceKm", e.target.value)}
            helperText="Leave blank to save an approximate distance from origin and destination addresses."
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label='Load description (e.g. "19 Ft Load")'
            variant="outlined"
            size="small"
            value={doc.loadDescription}
            onChange={(e) => update("loadDescription", e.target.value)}
            helperText={
              doc.suggestedTruckSize
                ? `Suggested from total CFT: ${doc.suggestedTruckSize}`
                : "Leave blank to use the CFT-based truck suggestion when you save from inventory."
            }
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Total CFT (PDF packing line)"
            variant="outlined"
            size="small"
            value={doc.totalCft}
            onChange={(e) => update("totalCft", e.target.value)}
            helperText="Usually matches inventory total"
          />
        </Box>
      </Box>

      <Typography className={classes.subSectionTitle}>Declared value &amp; GST</Typography>
      <Box className={classes.rowEq4}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label={`Declared luggage value (${currencySymbol()})`}
            variant="outlined"
            size="small"
            value={doc.declaredLuggageValue}
            onChange={(e) => update("declaredLuggageValue", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Carrier risk %"
            variant="outlined"
            size="small"
            value={doc.carrierRiskPercent}
            onChange={(e) => update("carrierRiskPercent", e.target.value)}
            helperText="Shown on PDF carrier-risk line (default 2)"
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="GST %"
            variant="outlined"
            size="small"
            value={doc.gstPercent}
            onChange={(e) => update("gstPercent", e.target.value)}
            helperText="Used when GST amount is blank"
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label={`GST amount (${currencySymbol()})`}
            variant="outlined"
            size="small"
            value={doc.gstAmount}
            onChange={(e) => update("gstAmount", e.target.value)}
            helperText="Overrides % when set"
          />
        </Box>
      </Box>

      <Typography className={classes.sectionTitle} variant="subtitle1">
        {`3. Charge amounts (${currencySymbol()})`}
      </Typography>
      <Box className={classes.chargeRow4}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Transportation charges"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.transportationChargesAmount}
            onChange={(e) => update("transportationChargesAmount", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Car transportation charges"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.carTransportationChargesAmount}
            onChange={(e) => update("carTransportationChargesAmount", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Loading charges"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.loadingChargesAmount}
            onChange={(e) => update("loadingChargesAmount", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Unloading charges"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.unloadingChargesAmount}
            onChange={(e) => update("unloadingChargesAmount", e.target.value)}
          />
        </Box>
      </Box>
      <Box className={classes.chargeRow3}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Unpacking / rearranging"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.unpackingRearrangingChargesAmount}
            onChange={(e) => update("unpackingRearrangingChargesAmount", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Shuttle / long carry / escort"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.shuttleLongCarryEscortChargesAmount}
            onChange={(e) => update("shuttleLongCarryEscortChargesAmount", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Carrier's risk charges"
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={doc.carrierRiskChargesAmount}
            onChange={(e) => update("carrierRiskChargesAmount", e.target.value)}
          />
        </Box>
      </Box>

      <Box className={classes.previewBox}>
        <Typography variant="subtitle2" gutterBottom>
          Live preview (2 decimals)
        </Typography>
        <Typography variant="body2">
          Subtotal: {currencySymbol()} {formatInr(preview.subTotal)} &nbsp;|&nbsp; GST: {currencySymbol()} {formatInr(preview.gstAmount)}{" "}
          &nbsp;|&nbsp; Total: {currencySymbol()} {formatInr(preview.totalAmount)}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
          Subtotal = sum of the seven charge fields. Total = subtotal + GST (GST amount wins over % when set).
        </Typography>
      </Box>

      <Typography className={classes.sectionTitle} variant="subtitle1">
        4. Notes &amp; terms
      </Typography>
      <Box className={classes.notesStack}>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Intro paragraph (PDF body)"
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            value={doc.quotationIntro}
            onChange={(e) => update("quotationIntro", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Payment terms"
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={doc.paymentTerms}
            onChange={(e) => update("paymentTerms", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Note (PDF Note section)"
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={doc.quotationPdfNote}
            onChange={(e) => update("quotationPdfNote", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Handyman services (shown in f) line)"
            variant="outlined"
            size="small"
            value={doc.handymanServicesNote}
            onChange={(e) => update("handymanServicesNote", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Extra handling & storage (under EXTRA HANDLING…)"
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={doc.extraHandlingAndStorageNote}
            onChange={(e) => update("extraHandlingAndStorageNote", e.target.value)}
          />
        </Box>
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Society / local union / octroi (after e) line)"
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={doc.localUnionOctroiNote}
            onChange={(e) => update("localUnionOctroiNote", e.target.value)}
          />
        </Box>
        <Box className={classes.notesTwoCol}>
          <Box className={classes.fieldCell}>
            <TextField
              fullWidth
              label="Optional extra note line 1"
              variant="outlined"
              size="small"
              value={doc.quotationExtraNote1}
              onChange={(e) => update("quotationExtraNote1", e.target.value)}
            />
          </Box>
          <Box className={classes.fieldCell}>
            <TextField
              fullWidth
              label="Optional extra note line 2"
              variant="outlined"
              size="small"
              value={doc.quotationExtraNote2}
              onChange={(e) => update("quotationExtraNote2", e.target.value)}
            />
          </Box>
        </Box>
        <Box className={classes.validityRow}>
          <Box className={classes.fieldCell}>
            <TextField
              fullWidth
              label="Validity (days)"
              variant="outlined"
              size="small"
              value={doc.validityDays}
              onChange={(e) => update("validityDays", e.target.value)}
            />
          </Box>
          <Box className={classes.fieldCell}>
            <TextField
              fullWidth
              label="Move window (days)"
              variant="outlined"
              size="small"
              value={doc.moveWindowDays}
              onChange={(e) => update("moveWindowDays", e.target.value)}
            />
          </Box>
          <Box className={classes.fieldCell}>
            <TextField
              fullWidth
              label="Transit time note"
              variant="outlined"
              size="small"
              value={doc.transitTimeNote}
              onChange={(e) => update("transitTimeNote", e.target.value)}
            />
          </Box>
        </Box>
        <Divider style={{ margin: "8px 0" }} />
        <Box className={classes.fieldCell}>
          <TextField
            fullWidth
            label="Terms & conditions (one per line, page 2)"
            variant="outlined"
            size="small"
            multiline
            minRows={8}
            value={doc.termsAndConditionsText}
            onChange={(e) => update("termsAndConditionsText", e.target.value)}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default QuotationPricingSection;
